import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useGlobalStore } from '../../stores/globalStore';
import { usePermission } from '../../hooks/usePermission';
import {
  PURCHASING_FEATURES_SETUP,
  featureLabel,
  featureOn,
  usePurchasingFeatures,
  type PurchasingFeature,
} from '../../api/purchasing/features';
import { PermissionsLoading } from './SessionLoadError';
import '../../pages/purchasing/SuppliersCatalog.css';

/**
 * A page that belongs to an optional purchasing feature (Purchasing Setup, Features). While the
 * feature is off for the facility the page is not shown: a plain one-line notice says so, with a
 * link to the switch for users who can open Purchasing Setup. Fails closed while the switches load.
 *
 * The menu and the top-bar search leave these pages out too (navigation/navConfig.ts); this covers
 * an old link or a typed address. The API still answers the feature's endpoints.
 */
export default function PurchasingFeatureRoute({
  feature,
  children,
}: {
  feature: PurchasingFeature | PurchasingFeature[];
  children: ReactNode;
}) {
  const facilityId = useGlobalStore((s) => s.facilityId);
  const { features, loaded } = usePurchasingFeatures(facilityId);
  const { canView } = usePermission();

  if (!loaded) return <PermissionsLoading />;
  if (featureOn(features, feature)) return <>{children}</>;
  return (
    <p className="purchasing-feature-off" role="status">
      {featureLabel(feature)} {Array.isArray(feature) && feature.length > 1 ? 'are' : 'is'} turned off for this facility.{' '}
      {canView('pUR_Tables_Level') ? (
        <Link to={PURCHASING_FEATURES_SETUP}>Turn it on in Purchasing Setup.</Link>
      ) : (
        'Turn it on in Purchasing Setup.'
      )}
    </p>
  );
}
